import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  BuildingOfficeIcon, 
  PlusIcon,
  CalendarIcon,
  UserGroupIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { LeagueService, SeasonService } from '../../../services/firebaseService';

export default function LeaguesList() {
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadLeagues();
  }, []);

  const loadLeagues = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [leaguesData, seasonsData] = await Promise.all([
        LeagueService.getAllLeagues(),
        SeasonService.getAllSeasons()
      ]);

      // Count seasons per league
      const leaguesWithSeasonCounts = leaguesData.map(league => {
        const leagueSeasons = seasonsData.filter(season => 
          season.leagues && season.leagues.includes(league.id)
        );
        
        return {
          ...league,
          seasonCount: leagueSeasons.length,
          seasons: leagueSeasons
        };
      });

      setLeagues(leaguesWithSeasonCounts);
    } catch (error) {
      console.error('Error loading leagues:', error);
      setError('Failed to load leagues. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-survivor-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading leagues...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Leagues</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadLeagues}
            className="px-4 py-2 bg-survivor-600 text-white rounded-md hover:bg-survivor-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <BuildingOfficeIcon className="h-8 w-8 text-gray-400 mr-3" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Leagues</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Manage all leagues and their seasons
                </p>
              </div>
            </div>
            <Link
              to="/admin/leagues/create"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create League
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="px-4 py-6 sm:px-0 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <BuildingOfficeIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Total Leagues
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {leagues.length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CalendarIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Total Seasons
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {leagues.reduce((sum, league) => sum + league.seasonCount, 0)}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <UserGroupIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Active Leagues
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {leagues.filter(league => league.status === 'active').length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Leagues List */}
        <div className="px-4 py-6 sm:px-0">
          {leagues.length === 0 ? (
            <div className="text-center py-12">
              <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No leagues</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by creating a new league.
              </p>
              <div className="mt-6">
                <Link
                  to="/admin/leagues/create"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Create League
                </Link>
              </div>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {leagues.map((league) => (
                  <li key={league.id}>
                    <Link
                      to={`/admin/leagues/${league.id}`}
                      className="block hover:bg-gray-50 px-4 py-4 sm:px-6"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <BuildingOfficeIcon className="h-8 w-8 text-survivor-600" />
                          </div>
                          <div className="ml-4">
                            <div className="flex items-center">
                              <p className="text-sm font-medium text-survivor-600 truncate">
                                {league.name}
                              </p>
                              <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                league.status === 'active' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {league.status}
                              </span>
                            </div>
                            <div className="mt-1">
                              <p className="text-sm text-gray-500">
                                {league.description || 'No description'}
                              </p>
                            </div>
                            <div className="mt-1 flex items-center text-sm text-gray-500">
                              <CalendarIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                              <span>{league.seasonCount} season{league.seasonCount !== 1 ? 's' : ''}</span>
                              <span className="mx-2">•</span>
                              <span>Created {formatDate(league.createdAt)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-900">
                              {league.maxTeams || 'Unlimited'} max teams
                            </p>
                            <p className="text-sm text-gray-500">
                              {league.currentTeams || 0} current teams
                            </p>
                          </div>
                          <div className="ml-4">
                            <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
